import React, { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';
import type { TutorSkill } from '../../models/Tutor';
import { tutorSkillLabel } from '../../utils/tutorSkillI18n';
import { getTutorSkillOptions, type TutorSkillOption } from '../../utils/tutorSkillOptions';

type CommonProps = {
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
  label?: string;
  placeholder?: string;
  id?: string;
  onKeyDown?: React.KeyboardEventHandler;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
};

type MultipleProps = CommonProps & {
  multiple: true;
  value: TutorSkill[];
  onChange: (skills: TutorSkill[]) => void;
  limitTags?: number;
};

type SingleProps = CommonProps & {
  multiple?: false;
  value: TutorSkill | '';
  onChange: (skill: TutorSkill | '') => void;
};

export type TutorSkillAutocompleteProps = MultipleProps | SingleProps;

function resolveSkillOptions(
  codes: TutorSkill[],
  skillOptions: TutorSkillOption[],
  t: ReturnType<typeof useTranslation>['t'],
  locale: string
): TutorSkillOption[] {
  const byCode = new Map(skillOptions.map((option) => [option.code, option]));
  return codes
    .map((code) => {
      const known = byCode.get(code);
      if (known) return known;
      return {
        code,
        label: tutorSkillLabel(t, code),
        section: 'all' as const,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label, locale.replace(/_/g, '-'), { sensitivity: 'base' }));
}

function renderSkillGroup(
  params: { key: string; group: string; children?: React.ReactNode },
  t: ReturnType<typeof useTranslation>['t']
) {
  const isPopular = params.group === 'popular';
  return (
    <li key={params.key} style={{ width: '100%', listStyle: 'none' }}>
      {isPopular ? (
        <Box
          sx={{
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.primary.main, 0.05),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            component="div"
            role="presentation"
            sx={{ px: 2, pt: 1.25, pb: 0.5, color: 'text.secondary' }}
          >
            <Typography
              variant="overline"
              sx={{ lineHeight: 1.5, display: 'block', letterSpacing: 0.8, fontWeight: 600 }}
            >
              {t('tutor.profileCompletion.skillsSectionPopular')}
            </Typography>
          </Box>
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              px: 1.5,
              pt: 0.5,
              pb: 1.5,
              listStyle: 'none',
              width: '100%',
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {params.children}
          </Box>
        </Box>
      ) : (
        <>
          <Box
            component="div"
            role="presentation"
            sx={{
              px: 2,
              py: 0.75,
              bgcolor: 'action.hover',
              color: 'text.secondary',
              borderBottom: 1,
              borderColor: 'divider',
              position: 'static',
            }}
          >
            <Typography variant="overline" sx={{ lineHeight: 1.5, display: 'block', letterSpacing: 1 }}>
              {t('tutor.profileCompletion.skillsSectionAll')}
            </Typography>
          </Box>
          <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none', width: '100%' }}>
            {params.children}
          </Box>
        </>
      )}
    </li>
  );
}

function renderSkillOption(
  props: React.HTMLAttributes<HTMLLIElement>,
  option: TutorSkillOption,
  selected: boolean,
  multiple: boolean
) {
  const isPopular = option.section === 'popular';
  return (
    <Box
      component="li"
      {...props}
      sx={{
        display: 'flex',
        alignItems: isPopular ? 'flex-start' : 'center',
        gap: isPopular ? 0.75 : 1,
        minWidth: 0,
        ...(isPopular
          ? {
              width: '100%',
              minHeight: 44,
              px: 1,
              py: 0.75,
              boxSizing: 'border-box',
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: selected ? 'action.selected' : 'background.paper',
              transition: (theme) =>
                theme.transitions.create(['background-color', 'border-color'], {
                  duration: theme.transitions.duration.shorter,
                }),
              '&:hover': {
                bgcolor: selected ? 'action.selected' : 'action.hover',
                borderColor: selected ? 'primary.main' : 'text.disabled',
              },
              '&.Mui-focused': {
                boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
              },
            }
          : {
              width: '100%',
              '&.MuiAutocomplete-option': { alignItems: 'center' },
            }),
      }}
    >
      {multiple ? (
        <Checkbox
          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
          checkedIcon={<CheckBoxIcon fontSize="small" />}
          sx={{ p: isPopular ? 0.25 : 0.5, flexShrink: 0 }}
          checked={selected}
          tabIndex={-1}
          disableRipple
        />
      ) : null}
      <Typography
        component="span"
        variant="body2"
        noWrap={!isPopular}
        title={option.label}
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: isPopular && selected ? 600 : 400,
          ...(isPopular
            ? {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.35,
              }
            : {}),
        }}
      >
        {option.label}
      </Typography>
    </Box>
  );
}

const TutorSkillAutocomplete: React.FC<TutorSkillAutocompleteProps> = (props) => {
  const { t, i18n } = useTranslation();
  const {
    fullWidth = true,
    size = 'medium',
    sx,
    textFieldSx,
    label,
    placeholder,
    id = 'tutor-skills',
    onKeyDown,
    disabled = false,
    required = false,
    error = false,
    helperText,
  } = props;

  const skillOptions = useMemo(() => getTutorSkillOptions(t, i18n.language), [t, i18n.language]);

  const selectedMultipleOptions = useMemo(() => {
    if (!props.multiple) return [] as TutorSkillOption[];
    return resolveSkillOptions(props.value, skillOptions, t, i18n.language);
  }, [props.multiple, props.multiple ? props.value : null, skillOptions, t, i18n.language]);

  const selectedSingleOption = useMemo((): TutorSkillOption | null => {
    if (props.multiple || !props.value) return null;
    const known = skillOptions.find((option) => option.code === props.value);
    if (known) return known;
    return {
      code: props.value,
      label: tutorSkillLabel(t, props.value),
      section: 'all',
    };
  }, [props.multiple, props.multiple ? null : props.value, skillOptions, t]);

  const listboxProps = {
    sx: {
      maxHeight: 320,
      p: 0,
      overflow: 'auto',
      '& .MuiAutocomplete-groupUl': { padding: 0 },
    },
  };

  const defaultLabel = t('tutor.profileCompletion.skillsLabel', t('tutor.profileCompletion.skills'));
  const defaultPlaceholder = t('tutor.profileCompletion.skillsPlaceholder');

  if (props.multiple) {
    return (
      <Autocomplete
        multiple
        fullWidth={fullWidth}
        size={size}
        id={id}
        disabled={disabled}
        options={skillOptions}
        groupBy={(option) => option.section}
        disableCloseOnSelect
        blurOnSelect={false}
        value={selectedMultipleOptions}
        onChange={(_, newValue) => props.onChange(newValue.map((option) => option.code))}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        getOptionLabel={(option) => option.label}
        limitTags={props.limitTags ?? 4}
        getLimitTagsText={(more) => t('tutor.profileCompletion.skillsMoreCount', { count: more })}
        ListboxProps={listboxProps}
        renderGroup={(params) => renderSkillGroup(params, t)}
        renderOption={(optionProps, option, { selected }) =>
          renderSkillOption(optionProps, option, selected, true)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label ?? defaultLabel}
            placeholder={placeholder ?? defaultPlaceholder}
            onKeyDown={onKeyDown}
            required={required}
            error={error}
            helperText={helperText}
            sx={textFieldSx}
          />
        )}
        sx={sx}
      />
    );
  }

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      id={id}
      disabled={disabled}
      options={skillOptions}
      groupBy={(option) => option.section}
      value={selectedSingleOption}
      onChange={(_, newValue) => props.onChange(newValue?.code ?? '')}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      getOptionLabel={(option) => option.label}
      ListboxProps={listboxProps}
      renderGroup={(params) => renderSkillGroup(params, t)}
      renderOption={(optionProps, option, { selected }) =>
        renderSkillOption(optionProps, option, selected, false)
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? defaultLabel}
          placeholder={placeholder ?? defaultPlaceholder}
          onKeyDown={onKeyDown}
          required={required}
          error={error}
          helperText={helperText}
          sx={textFieldSx}
        />
      )}
      sx={sx}
    />
  );
};

export default TutorSkillAutocomplete;
